import { useState } from 'react';
import type { ReactNode } from 'react';
import { useAction, useApi, useRead } from './context';
import { Confirm, Empty, Failure, Loading, Rich, Section, StatePill, When } from './bits';
import { Segmented } from './kit';
import { useT } from '../messages';
import { sentenceOf } from '../api/errors';
import type { ApiFailure } from '../api/errors';
import type { JobKind } from '../wire';

/**
 * Conformance runs and import jobs — triggered here, executed elsewhere.
 *
 * Neither belongs *in* a request. A conformance run is `packages/contracts`
 * executing ~70 obligations against a base URL and an import is a LoveSpa
 * SQLite file becoming a tenant's year of history (spec §9): minutes of work, an
 * unbounded memory profile, and a failure mode that must not be "the operator's
 * browser tab closed". So each is a Cloud Run Job with its own service account
 * and its own timeout, and this screen holds the four verbs an operator needs —
 * run it, watch it, list what ran, read one execution.
 *
 * Two things this screen refuses to soften:
 *
 *  * **An import is a dry run unless it says otherwise.** Spec §9's gate is
 *    dry-run-by-default, and a console is exactly the surface where that default
 *    quietly inverts — one pre-ticked box and a year of somebody's history is
 *    written by accident. So the choice is a pair of radio buttons that start on
 *    dry run, executing is styled as the dangerous thing it is, and it takes a
 *    confirm that says what will be written.
 *  * **"Unconfigured" is a state, not an error.** `CONFORMANCE_JOB_NAME` and
 *    `IMPORT_JOB_NAME` are optional on purpose, so a console can be deployed
 *    before the jobs exist — and be the console you use to finish setting them
 *    up. When the server answers `503` naming the missing variable, this screen
 *    renders that sentence as a fact about the environment rather than as
 *    something that broke.
 *
 * The logs are not mirrored here and will not be. A job's output lives in Cloud
 * Logging under its own execution id; copying it through this service would put
 * an import's row-by-row output — which is tenant data — onto the one surface
 * that must never hold any (spec §6.3).
 */
export function Jobs({ tenantId }: { tenantId?: string | undefined }): ReactNode {
  const t = useT();
  const [job, setJob] = useState<JobKind>('conformance');
  // Bumped when a run starts, so the executions table re-reads once rather than
  // each panel keeping a second copy of the same list.
  const [ran, setRan] = useState(0);
  const started = (): void => setRan((n) => n + 1);

  return (
    <div className="view">
      <header className="view__head">
        <h2>{t('jobs.title')}</h2>
        <nav className="view__actions" aria-label={t('jobs.navLabel')}>
          <button
            type="button"
            className={`button${job === 'conformance' ? ' is-current' : ''}`}
            onClick={() => setJob('conformance')}
          >
            {t('jobs.tab.conformance')}
          </button>
          <button
            type="button"
            className={`button${job === 'import' ? ' is-current' : ''}`}
            onClick={() => setJob('import')}
          >
            {t('jobs.tab.import')}
          </button>
        </nav>
      </header>

      {job === 'conformance' ? (
        <ConformancePanel tenantId={tenantId} onStarted={started} ran={ran} />
      ) : (
        <ImportPanel tenantId={tenantId} onStarted={started} ran={ran} />
      )}
    </div>
  );
}

/** `503` naming a missing job variable is an environment fact, not a fault. */
function unconfiguredMessage(failure: ApiFailure | null): string | null {
  if (failure === null) return null;
  if (failure.code !== 'unavailable') return null;
  return /job is configured|is not configured/.test(failure.message) ? failure.message : null;
}

function Unconfigured({ message }: { message: string }): ReactNode {
  const t = useT();
  return (
    <div className="by-design" role="note">
      <h4>{t('jobs.unconfigured.title')}</h4>
      {/* The `503`'s own sentence, which names the missing variable. */}
      <p>{message}</p>
      <p className="by-design__rule">{t('jobs.unconfigured.rule')}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Conformance                                                         */
/* ------------------------------------------------------------------ */

function ConformancePanel({
  tenantId,
  onStarted,
  ran,
}: {
  tenantId?: string | undefined;
  onStarted: () => void;
  ran: number;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const action = useAction();
  const [tenant, setTenant] = useState(tenantId ?? '');
  const [baseUrl, setBaseUrl] = useState('');
  const [started, setStarted] = useState<string | null>(null);
  const unconfigured = unconfiguredMessage(action.failure);

  const run = async (): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.runJob('conformance', {
        tenant_id: tenant,
        ...(baseUrl === '' ? {} : { base_url: baseUrl }),
      });
      setStarted(
        t('jobs.started', {
          tenantId: answer.tenant_id,
          // Either the server's own note, or the execution id it minted.
          detail:
            answer.execution === null
              ? answer.note
              : t('jobs.execution', { executionId: answer.execution.execution_id }),
        }),
      );
    });
    if (ok) onStarted();
  };

  return (
    <>
      <Section title={t('jobs.conformance.title')} note={t('jobs.conformance.note')}>
        {unconfigured !== null ? <Unconfigured message={unconfigured} /> : null}

        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            void run();
          }}
        >
          <div className="form__row">
            <TenantPicker value={tenant} onChange={setTenant} />
            <label className="field field--wide">
              <span className="field__label">{t('jobs.baseUrl')}</span>
              <input
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                aria-label={t('jobs.baseUrl')}
                placeholder={t('jobs.baseUrlPlaceholder')}
              />
            </label>
          </div>

          {unconfigured === null && action.failure !== null ? (
            <Failure failure={action.failure} />
          ) : null}
          {started !== null ? <p className="ok">{started}</p> : null}

          <div className="row-actions">
            <button
              type="submit"
              className="button button--primary"
              disabled={action.busy || tenant === ''}
            >
              {action.busy ? t('common.starting') : t('jobs.run')}
            </button>
          </div>
        </form>
      </Section>

      <Executions job="conformance" ran={ran} tenantId={tenantId} />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Import                                                              */
/* ------------------------------------------------------------------ */

function ImportPanel({
  tenantId,
  onStarted,
  ran,
}: {
  tenantId?: string | undefined;
  onStarted: () => void;
  ran: number;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const action = useAction();
  const [tenant, setTenant] = useState(tenantId ?? '');
  const [file, setFile] = useState('');
  const [execute, setExecute] = useState(false);
  const [asking, setAsking] = useState(false);
  const [started, setStarted] = useState<string | null>(null);
  const unconfigured = unconfiguredMessage(action.failure);

  const start = async (): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.runJob('import', {
        tenant_id: tenant,
        execute,
        ...(file === '' ? {} : { file }),
      });
      const said = {
        tenantId: answer.tenant_id,
        detail:
          answer.execution === null
            ? answer.note
            : t('jobs.execution', { executionId: answer.execution.execution_id }),
      };
      setStarted(
        answer.dry_run === true
          ? t('jobs.import.startedDry', said)
          : t('jobs.import.startedExecute', said),
      );
    });
    if (ok) {
      setAsking(false);
      onStarted();
    }
  };

  return (
    <>
      <Section title={t('jobs.import.title')} note={t('jobs.import.note')}>
        {unconfigured !== null ? <Unconfigured message={unconfigured} /> : null}

        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (execute) setAsking(true);
            else void start();
          }}
        >
          <div className="form__row">
            <TenantPicker value={tenant} onChange={setTenant} />
            <label className="field field--wide">
              <span className="field__label">{t('jobs.import.file')}</span>
              <input
                value={file}
                onChange={(e) => setFile(e.target.value)}
                aria-label={t('jobs.import.file')}
                placeholder="gs://yourstore-imports/lovespa-2026-08-25.sqlite"
              />
              <span className="field__hint">{t('jobs.import.fileHint')}</span>
            </label>
          </div>

          <fieldset className={`mode${execute ? ' mode--execute' : ''}`}>
            <legend>{t('jobs.import.mode')}</legend>
            <label className="mode__choice">
              <input
                type="radio"
                name="import-mode"
                checked={!execute}
                onChange={() => setExecute(false)}
              />
              <span>
                <Rich text={t('jobs.import.dryRun')} />
              </span>
            </label>
            <label className="mode__choice">
              <input
                type="radio"
                name="import-mode"
                checked={execute}
                onChange={() => setExecute(true)}
              />
              <span>
                <Rich text={t('jobs.import.execute')} />
              </span>
            </label>
          </fieldset>

          {unconfigured === null && action.failure !== null ? (
            <Failure failure={action.failure} />
          ) : null}
          {started !== null ? <p className={execute ? 'warn' : 'ok'}>{started}</p> : null}

          <div className="row-actions">
            <button
              type="submit"
              className={`button ${execute ? 'button--danger' : 'button--primary'}`}
              disabled={action.busy || tenant === ''}
            >
              {action.busy
                ? t('common.starting')
                : execute
                  ? t('jobs.import.startExecute')
                  : t('jobs.import.startDry')}
            </button>
          </div>
        </form>
      </Section>

      <Executions job="import" ran={ran} tenantId={tenantId} />

      {asking ? (
        <Confirm
          title={t('jobs.import.confirmTitle', { tenantId: tenant })}
          consequence={t('jobs.import.confirmConsequence')}
          confirmLabel={t('jobs.import.confirmLabel')}
          busy={action.busy}
          failure={action.failure}
          onConfirm={() => void start()}
          onCancel={() => {
            action.clear();
            setAsking(false);
          }}
        />
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Executions                                                          */
/* ------------------------------------------------------------------ */

/**
 * **Whose runs.** Opened from a business, the list shows that business's runs
 * by default — and it has to work that out itself, because Cloud Run does not
 * record which tenant a run was for: the tenant rides the run as an argument or
 * an environment variable, and the executions listing returns neither. What
 * does record it is this console's own `job.run` audit row, written with the
 * tenant and `<job>:<execution id>` as its target when the run starts. So the
 * filter is a join, client-side, against that business's `job.run` rows — one
 * more audited read, only when the view is narrowed — and "Every business"
 * stays one click away, because a run started from a terminal has no such row
 * and would otherwise be invisible here.
 */
function Executions({
  job,
  ran,
  tenantId,
}: {
  job: JobKind;
  ran: number;
  tenantId?: string | undefined;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const list = useRead(() => api.executions(job), [job, ran]);
  const [open, setOpen] = useState<string | null>(null);
  const [scope, setScope] = useState<'tenant' | 'all'>(tenantId === undefined ? 'all' : 'tenant');
  const narrowed = tenantId !== undefined && scope === 'tenant';
  const started = useRead(
    () =>
      narrowed
        ? api.audit({ tenant_id: tenantId, action: 'job.run', limit: 200 })
        : Promise.resolve(null),
    [narrowed, tenantId, job, ran],
  );
  const ours = new Set(
    (started.value?.entries ?? [])
      .filter((row) => row.target.startsWith(`${job}:`))
      .map((row) => row.target.slice(job.length + 1)),
  );
  const shown =
    list.value === null
      ? null
      : narrowed
        ? list.value.executions.filter((e) => ours.has(e.execution_id))
        : list.value.executions;
  const unconfigured = unconfiguredMessage(list.failure);

  return (
    <Section
      title={t('jobs.executions.title')}
      note={t('jobs.executions.note')}
      actions={
        <button type="button" className="button" onClick={list.reload} disabled={list.loading}>
          {t('common.refresh')}
        </button>
      }
    >
      {tenantId === undefined ? null : (
        <div className="row-actions">
          <Segmented
            label={t('jobs.executions.scopeLabel')}
            options={[
              { id: 'tenant', label: t('jobs.executions.scope.tenant', { tenantId }) },
              { id: 'all', label: t('jobs.executions.scope.all') },
            ]}
            value={scope}
            onChange={setScope}
          />
          {narrowed ? <span className="muted">{t('jobs.executions.scopeNote')}</span> : null}
        </div>
      )}

      {unconfigured !== null ? (
        <Unconfigured message={unconfigured} />
      ) : list.failure !== null ? (
        <Failure failure={list.failure} />
      ) : null}
      {narrowed && started.failure !== null ? <Failure failure={started.failure} /> : null}

      {shown === null || (narrowed && started.value === null && started.loading) ? (
        list.loading || started.loading ? (
          <Loading what={t('loading.executions')} />
        ) : null
      ) : list.value?.executions.length === 0 ? (
        <Empty>{t('jobs.executions.empty')}</Empty>
      ) : shown.length === 0 ? (
        <Empty>{t('jobs.executions.scopeEmpty')}</Empty>
      ) : (
        <table className="table table--dense">
          <thead>
            <tr>
              <th>{t('jobs.th.execution')}</th>
              <th>{t('jobs.th.state')}</th>
              <th>{t('jobs.th.started')}</th>
              <th>{t('jobs.th.completed')}</th>
              <th>{t('jobs.th.tasks')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {shown.map((e) => (
              <tr key={e.execution_id}>
                <td>
                  <code>{e.execution_id}</code>
                </td>
                <td>
                  <StatePill state={e.state} />
                </td>
                <td>
                  <When iso={e.started_at} />
                </td>
                <td>
                  <When iso={e.completed_at} empty={t('jobs.running')} />
                </td>
                <td>
                  {t('jobs.tasks', {
                    succeeded: e.succeeded_count ?? 0,
                    total: e.task_count ?? 0,
                  })}
                  {(e.failed_count ?? 0) > 0 ? (
                    <span className="flag flag--bad">
                      {t('jobs.failedFlag', { count: e.failed_count ?? 0 })}
                    </span>
                  ) : null}
                </td>
                <td>
                  <button
                    type="button"
                    className="linkish"
                    onClick={() => setOpen(open === e.execution_id ? null : e.execution_id)}
                  >
                    {open === e.execution_id ? t('common.hide') : t('jobs.detail')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {open !== null ? <ExecutionDetailPanel job={job} executionId={open} /> : null}
    </Section>
  );
}

function ExecutionDetailPanel({
  job,
  executionId,
}: {
  job: JobKind;
  executionId: string;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const detail = useRead(() => api.execution(job, executionId), [job, executionId]);

  return (
    <div className="panel">
      {detail.failure !== null ? <Failure failure={detail.failure} /> : null}
      {detail.value === null ? (
        detail.loading ? (
          <Loading what={t('loading.execution')} />
        ) : null
      ) : (
        <>
          <dl className="facts">
            <div>
              <dt>{t('jobs.th.execution')}</dt>
              <dd>
                <code>{detail.value.execution.execution_id}</code>
              </dd>
            </div>
            <div>
              <dt>{t('jobs.dt.resource')}</dt>
              <dd className="wrap muted">{detail.value.execution.name}</dd>
            </div>
            <div>
              <dt>{t('jobs.th.state')}</dt>
              <dd>
                <StatePill state={detail.value.execution.state} />
              </dd>
            </div>
            <div>
              <dt>{t('jobs.dt.created')}</dt>
              <dd>
                <When iso={detail.value.execution.created_at} />
              </dd>
            </div>
            <div>
              <dt>{t('jobs.th.started')}</dt>
              <dd>
                <When iso={detail.value.execution.started_at} />
              </dd>
            </div>
            <div>
              <dt>{t('jobs.th.completed')}</dt>
              <dd>
                <When iso={detail.value.execution.completed_at} empty={t('jobs.running')} />
              </dd>
            </div>
          </dl>
          <p className="muted">{t('jobs.logsElsewhere')}</p>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The tenant picker                                                   */
/* ------------------------------------------------------------------ */

/**
 * A picker rather than a text box, because `admin-api` checks the tenant exists
 * before it starts a job and a typo is fifteen minutes of red that means
 * nothing. The list is the fleet read, which the operator is entitled to.
 */
export function TenantPicker({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (tenantId: string) => void;
  label?: string;
}): ReactNode {
  const api = useApi();
  const t = useT();
  // Test tenants included: the conformance run's own fixtures are `t_test_*`,
  // and a picker that hid them was a picker that could not pick the tenant a
  // conformance run is most often for.
  const fleet = useRead(() => api.fleet({ limit: 200, includeTest: true }), []);
  const name = label ?? t('common.tenant');

  return (
    <label className="field">
      <span className="field__label">{name}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={name}>
        <option value="">{t('jobs.picker.choose')}</option>
        {/* The business this screen was opened from, even when it is not on
            the first page of the fleet — a picker that silently showed
            "choose" while holding a tenant would run against one nobody saw. */}
        {value !== '' && !(fleet.value?.tenants ?? []).some((row) => row.tenant_id === value) ? (
          <option value={value}>{value}</option>
        ) : null}
        {/* Slug, tenant name and status: three pieces of data and no words. */}
        {(fleet.value?.tenants ?? []).map((row) => (
          <option key={row.tenant_id} value={row.tenant_id}>
            {row.tenant_id} — {row.name} ({row.status})
          </option>
        ))}
      </select>
      {fleet.failure !== null ? (
        <span className="field__hint">
          {t('jobs.picker.failed', { message: sentenceOf(fleet.failure, t) })}
        </span>
      ) : null}
    </label>
  );
}
